import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, Search, Plus, Minus, X, ShoppingBag, Leaf, WheatOff, ChefHat, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Spice = ({ level }) => {
  if (!level) return null;
  return (
    <span className="flex items-center gap-0.5" title={`Spice level ${level}/3`}>
      {Array.from({ length: level }).map((_, i) => (
        <Flame key={i} className="w-3.5 h-3.5 text-terracotta fill-terracotta/30" />
      ))}
    </span>
  );
};

const TagIcon = ({ tag }) => {
  if (tag === "vegetarian") return <Leaf className="w-3 h-3" />;
  if (tag === "gluten-free") return <WheatOff className="w-3 h-3" />;
  return <ChefHat className="w-3 h-3" />;
};

export default function MenuSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");
  const [diet, setDiet] = useState(null);
  const [selected, setSelected] = useState(null);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkout, setCheckout] = useState({ name: "", phone: "" });
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/menu`)
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Could not load the menu"))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => ["All", ...new Set(items.map((i) => i.category))], [items]);

  const filtered = useMemo(
    () =>
      items.filter((i) => {
        if (category !== "All" && i.category !== category) return false;
        if (diet && !(i.tags || []).includes(diet)) return false;
        if (query && !`${i.name} ${i.description}`.toLowerCase().includes(query.toLowerCase())) return false;
        return true;
      }),
    [items, category, diet, query]
  );

  const addToCart = (item) => {
    setCart((c) => {
      const ex = c.find((x) => x.id === item.id);
      if (ex) return c.map((x) => (x.id === item.id ? { ...x, qty: x.qty + 1 } : x));
      return [...c, { ...item, qty: 1 }];
    });
    toast.success(`${item.name} added to your order`, { duration: 1800 });
  };

  const changeQty = (id, d) =>
    setCart((c) =>
      c.map((x) => (x.id === id ? { ...x, qty: Math.max(0, x.qty + d) } : x)).filter((x) => x.qty > 0)
    );

  const total = cart.reduce((s, x) => s + x.price * x.qty, 0);
  const count = cart.reduce((s, x) => s + x.qty, 0);

  const placeOrder = async (e) => {
    e.preventDefault();
    if (!cart.length) return;
    setPlacing(true);
    try {
      await axios.post(`${API}/orders`, {
        customer_name: checkout.name,
        phone: checkout.phone,
        items: cart.map((x) => ({ name: x.name, price: x.price, qty: x.qty })),
        total: Number(total.toFixed(2)),
      });
      toast.success("Order received! We'll call you to confirm pickup time.");
      setCart([]);
      setCartOpen(false);
      setCheckout({ name: "", phone: "" });
    } catch {
      toast.error("Could not place the order. Please call us instead.");
    } finally {
      setPlacing(false);
    }
  };

  return (
    <section id="menu" className="relative py-28 sm:py-36 bg-clay-900/40" data-testid="menu-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14"
        >
          <div>
            <p className="font-mono-d text-[11px] uppercase tracking-[0.3em] text-terracotta mb-6" data-testid="menu-overline">
              The Kitchen — $10–20 per person
            </p>
            <h2 className="font-serif-d text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-cream leading-[1.05]" data-testid="menu-title">
              A menu written <span className="italic text-terracotta">by generations</span>
            </h2>
          </div>
          <div className="relative w-full lg:w-80">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-cream/40" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search cravings…"
              className="w-full bg-clay-800 border border-terracotta/15 rounded-full pl-11 pr-4 py-3 text-sm text-cream placeholder:text-cream/35 focus:outline-none focus:border-terracotta/60 transition-colors duration-300"
              data-testid="menu-search-input"
            />
          </div>
        </motion.div>

        <div className="flex flex-wrap items-center gap-2.5 mb-6" data-testid="menu-category-tabs">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`px-5 py-2 rounded-full text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                category === c
                  ? "bg-terracotta text-obsidian"
                  : "bg-clay-800 text-cream/60 hover:text-cream border border-terracotta/10"
              }`}
              data-testid={`menu-tab-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2.5 mb-12" data-testid="menu-diet-filters">
          {[
            { key: "vegetarian", label: "Vegetarian", icon: Leaf },
            { key: "gluten-free", label: "Gluten-Free", icon: WheatOff },
            { key: "chef-special", label: "Chef Special", icon: ChefHat },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setDiet(diet === f.key ? null : f.key)}
              className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-mono-d uppercase tracking-[0.12em] transition-colors duration-300 border ${
                diet === f.key
                  ? "border-amber-warm text-amber-warm bg-amber-warm/10"
                  : "border-cream/15 text-cream/50 hover:text-cream/80"
              }`}
              data-testid={`menu-filter-${f.key}`}
            >
              <f.icon className="w-3.5 h-3.5" /> {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="menu-loading">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-72 rounded-2xl bg-clay-800 animate-pulse" />
            ))}
          </div>
        ) : (
          <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="menu-grid">
            <AnimatePresence mode="popLayout">
              {filtered.map((item) => (
                <motion.article
                  layout
                  key={item.id}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.35 }}
                  className="group relative bg-clay-800 border border-terracotta/10 rounded-2xl overflow-hidden hover:border-terracotta/35 transition-colors duration-500 cursor-pointer"
                  onClick={() => setSelected(item)}
                  data-testid={`menu-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {item.image && (
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.name}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-clay-800 via-transparent to-transparent" />
                      <span className="absolute top-3 right-3 px-3 py-1 rounded-full bg-obsidian/70 backdrop-blur-md font-mono-d text-xs text-amber-warm" data-testid={`menu-price-${item.id}`}>
                        ${item.price.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <h3 className="font-serif-d text-xl font-semibold text-cream leading-snug">{item.name}</h3>
                      <Spice level={item.spice} />
                    </div>
                    <p className="text-sm text-cream/55 font-light leading-relaxed line-clamp-2 mb-4">{item.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex gap-1.5">
                        {(item.tags || []).map((t) => (
                          <span key={t} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-agave/25 text-[10px] font-mono-d uppercase tracking-wider text-cream/60">
                            <TagIcon tag={t} /> {t.replace("-", " ")}
                          </span>
                        ))}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); addToCart(item); }}
                        className="w-9 h-9 rounded-full bg-terracotta/15 border border-terracotta/30 flex items-center justify-center text-terracotta hover:bg-terracotta hover:text-obsidian transition-colors duration-300"
                        data-testid={`menu-add-${item.id}`}
                        aria-label={`Add ${item.name} to order`}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
        {!loading && filtered.length === 0 && (
          <p className="text-center text-cream/50 py-20 font-light" data-testid="menu-empty">
            Nothing matches that craving — try clearing the filters.
          </p>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="bg-clay-900 border-terracotta/20 text-cream max-w-lg" data-testid="menu-item-modal">
          {selected && (
            <>
              {selected.image && (
                <div className="-mx-6 -mt-6 mb-4 h-56 overflow-hidden rounded-t-lg">
                  <img src={selected.image} alt={selected.name} className="w-full h-full object-cover" />
                </div>
              )}
              <DialogHeader>
                <DialogTitle className="font-serif-d text-3xl font-semibold text-cream">{selected.name}</DialogTitle>
                <DialogDescription className="text-cream/60 font-light leading-relaxed pt-2">
                  {selected.description}
                </DialogDescription>
              </DialogHeader>
              <div className="flex items-center justify-between pt-4">
                <div className="flex items-center gap-3">
                  <span className="font-mono-d text-2xl text-amber-warm" data-testid="menu-modal-price">${selected.price.toFixed(2)}</span>
                  <Spice level={selected.spice} />
                </div>
                <button
                  onClick={() => { addToCart(selected); setSelected(null); }}
                  className="px-6 py-2.5 rounded-full bg-terracotta text-obsidian text-sm font-semibold hover:bg-terracotta-hover transition-colors duration-300"
                  data-testid="menu-modal-add-btn"
                >
                  Add to Order
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AnimatePresence>
        {count > 0 && !cartOpen && (
          <motion.button
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            onClick={() => setCartOpen(true)}
            className="fixed bottom-6 right-6 z-40 flex items-center gap-3 px-6 py-4 rounded-full bg-terracotta text-obsidian shadow-2xl shadow-terracotta/25 hover:bg-terracotta-hover transition-colors duration-300"
            data-testid="cart-open-btn"
          >
            <ShoppingBag className="w-5 h-5" />
            <span className="font-semibold text-sm">{count} item{count > 1 ? "s" : ""} · ${total.toFixed(2)}</span>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {cartOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[70] bg-obsidian/70 backdrop-blur-sm"
              onClick={() => setCartOpen(false)}
            />
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 240 }}
              className="fixed top-0 right-0 bottom-0 z-[80] w-full max-w-md bg-clay-900 border-l border-terracotta/20 flex flex-col"
              data-testid="cart-drawer"
            >
              <div className="flex items-center justify-between p-6 border-b border-terracotta/15">
                <h3 className="font-serif-d text-2xl font-semibold text-cream">Your Order</h3>
                <button onClick={() => setCartOpen(false)} className="text-cream/60 hover:text-cream p-1" data-testid="cart-close-btn" aria-label="Close order">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {cart.length === 0 && <p className="text-cream/50 font-light" data-testid="cart-empty">Your order is empty — the fajitas are waiting.</p>}
                {cart.map((x) => (
                  <div key={x.id} className="flex items-center gap-4 bg-clay-800 rounded-xl p-4" data-testid={`cart-item-${x.id}`}>
                    <div className="flex-1">
                      <p className="font-medium text-cream text-sm">{x.name}</p>
                      <p className="font-mono-d text-xs text-amber-warm mt-1">${(x.price * x.qty).toFixed(2)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => changeQty(x.id, -1)} className="w-7 h-7 rounded-full border border-cream/20 text-cream/70 flex items-center justify-center hover:border-terracotta hover:text-terracotta transition-colors" data-testid={`cart-minus-${x.id}`} aria-label="Decrease">
                        {x.qty === 1 ? <Trash2 className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                      </button>
                      <span className="w-6 text-center text-sm text-cream font-medium">{x.qty}</span>
                      <button onClick={() => changeQty(x.id, 1)} className="w-7 h-7 rounded-full border border-cream/20 text-cream/70 flex items-center justify-center hover:border-terracotta hover:text-terracotta transition-colors" data-testid={`cart-plus-${x.id}`} aria-label="Increase">
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {cart.length > 0 && (
                <form onSubmit={placeOrder} className="p-6 border-t border-terracotta/15 space-y-3" data-testid="cart-checkout-form">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-mono-d text-xs uppercase tracking-[0.2em] text-cream/50">Total</span>
                    <span className="font-serif-d text-3xl font-semibold text-amber-warm" data-testid="cart-total">${total.toFixed(2)}</span>
                  </div>
                  <input
                    required
                    value={checkout.name}
                    onChange={(e) => setCheckout({ ...checkout, name: e.target.value })}
                    placeholder="Your name"
                    className="w-full bg-clay-800 border border-terracotta/15 rounded-xl px-4 py-3 text-sm text-cream placeholder:text-cream/35 focus:outline-none focus:border-terracotta/60 transition-colors"
                    data-testid="cart-name-input"
                  />
                  <input
                    required
                    value={checkout.phone}
                    onChange={(e) => setCheckout({ ...checkout, phone: e.target.value })}
                    placeholder="Phone for pickup confirmation"
                    className="w-full bg-clay-800 border border-terracotta/15 rounded-xl px-4 py-3 text-sm text-cream placeholder:text-cream/35 focus:outline-none focus:border-terracotta/60 transition-colors"
                    data-testid="cart-phone-input"
                  />
                  <button
                    type="submit"
                    disabled={placing}
                    className="w-full py-4 rounded-full bg-terracotta text-obsidian font-semibold text-sm hover:bg-terracotta-hover transition-colors duration-300 disabled:opacity-60"
                    data-testid="cart-place-order-btn"
                  >
                    {placing ? "Placing order…" : "Place Pickup Order"}
                  </button>
                </form>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </section>
  );
}
